/**
 * Mulch & Stuff Landscape Material Project Planner
 * Phase 2 — State, normalization, and core cubic-yard foundation
 *
 * This module is intentionally independent of Shopify DOM rendering.
 * Authoritative inputs live in project state.
 * Derived calculation results are recomputed from those inputs.
 */
(function (root, factory) {
  'use strict';

  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.MSProjectPlannerCore = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const SCHEMA_VERSION = 3;
  const CONFIG_REVISION = 'phase2-multiple-areas-1';

  const MEASUREMENT_STATUS = Object.freeze({
    INCOMPLETE: 'incomplete',
    INVALID: 'invalid',
    VALID: 'valid'
  });

  const PERIMETER_KEYS = Object.freeze([
    'totalFeet',
    'insideFeet',
    'outsideFeet'
  ]);

  function createId(prefix) {
    if (
      typeof globalThis !== 'undefined' &&
      globalThis.crypto &&
      typeof globalThis.crypto.randomUUID === 'function'
    ) {
      return `${prefix}-${globalThis.crypto.randomUUID()}`;
    }

    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  function toFiniteNumber(value) {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const number = Number(value);

    return Number.isFinite(number) ? number : NaN;
  }

  function normalizeDepthInches(value) {
    const depthInches = toFiniteNumber(value);

    if (depthInches === null) {
      return Object.freeze({
        status: MEASUREMENT_STATUS.INCOMPLETE,
        depthInches: null,
        depthFeet: null
      });
    }

    if (
      !Number.isFinite(depthInches) ||
      depthInches <= 0 ||
      !Number.isInteger(depthInches)
    ) {
      return Object.freeze({
        status: MEASUREMENT_STATUS.INVALID,
        depthInches: null,
        depthFeet: null
      });
    }

    return Object.freeze({
      status: MEASUREMENT_STATUS.VALID,
      depthInches,
      depthFeet: depthInches / 12
    });
  }

  function normalizePerimeterFacts(perimeterFacts) {
    if (perimeterFacts === null || perimeterFacts === undefined) {
      return {
        status: MEASUREMENT_STATUS.VALID,
        facts: {}
      };
    }

    if (
      typeof perimeterFacts !== 'object' ||
      Array.isArray(perimeterFacts)
    ) {
      return {
        status: MEASUREMENT_STATUS.INVALID,
        facts: {}
      };
    }

    const facts = {};

    for (const key of PERIMETER_KEYS) {
      if (
        !Object.prototype.hasOwnProperty.call(perimeterFacts, key) ||
        perimeterFacts[key] === null ||
        perimeterFacts[key] === undefined ||
        perimeterFacts[key] === ''
      ) {
        continue;
      }

      const value = Number(perimeterFacts[key]);

      if (!Number.isFinite(value) || value <= 0) {
        return {
          status: MEASUREMENT_STATUS.INVALID,
          facts: {}
        };
      }

      facts[key] = value;
    }

    return {
      status: MEASUREMENT_STATUS.VALID,
      facts
    };
  }

  /**
   * Creates the geometry-independent contract consumed by the core engine.
   *
   * No perimeter is inferred. Only legitimate perimeter facts explicitly
   * supplied by a measurement source are retained.
   */
  function normalizeMeasurement(input) {
    const source = input || {};
    const squareFeet = toFiniteNumber(source.squareFeet);

    if (squareFeet === null) {
      return Object.freeze({
        status: MEASUREMENT_STATUS.INCOMPLETE,
        squareFeet: null,
        perimeterFacts: Object.freeze({})
      });
    }

    if (!Number.isFinite(squareFeet) || squareFeet <= 0) {
      return Object.freeze({
        status: MEASUREMENT_STATUS.INVALID,
        squareFeet: null,
        perimeterFacts: Object.freeze({})
      });
    }

    const normalizedPerimeters = normalizePerimeterFacts(
      source.perimeterFacts
    );

    if (normalizedPerimeters.status !== MEASUREMENT_STATUS.VALID) {
      return Object.freeze({
        status: MEASUREMENT_STATUS.INVALID,
        squareFeet: null,
        perimeterFacts: Object.freeze({})
      });
    }

    return Object.freeze({
      status: MEASUREMENT_STATUS.VALID,
      squareFeet,
      perimeterFacts: Object.freeze({
        ...normalizedPerimeters.facts
      })
    });
  }

  /**
   * Authoritative cubic-yard calculation:
   *
   * cubic yards = square feet × depth in feet ÷ 27
   *
   * No waste, contingency, compaction allowance, hidden overage,
   * minimum quantity, or upward rounding is applied.
   */
  function calculateCubicYards(normalizedMeasurement, depthInchesInput) {
    if (
      !normalizedMeasurement ||
      normalizedMeasurement.status === MEASUREMENT_STATUS.INCOMPLETE
    ) {
      return Object.freeze({
        status: MEASUREMENT_STATUS.INCOMPLETE,
        cubicYards: null
      });
    }

    if (
      normalizedMeasurement.status !== MEASUREMENT_STATUS.VALID ||
      !Number.isFinite(normalizedMeasurement.squareFeet) ||
      normalizedMeasurement.squareFeet <= 0
    ) {
      return Object.freeze({
        status: MEASUREMENT_STATUS.INVALID,
        cubicYards: null
      });
    }

    const normalizedDepth = normalizeDepthInches(depthInchesInput);

    if (normalizedDepth.status !== MEASUREMENT_STATUS.VALID) {
      return Object.freeze({
        status: normalizedDepth.status,
        cubicYards: null
      });
    }

    const cubicYards =
      normalizedMeasurement.squareFeet *
      normalizedDepth.depthFeet /
      27;

    if (!Number.isFinite(cubicYards)) {
      return Object.freeze({
        status: MEASUREMENT_STATUS.INVALID,
        cubicYards: null
      });
    }

    return Object.freeze({
      status: MEASUREMENT_STATUS.VALID,
      squareFeet: normalizedMeasurement.squareFeet,
      depthInches: normalizedDepth.depthInches,
      depthFeet: normalizedDepth.depthFeet,
      cubicYards
    });
  }

  /**
   * Presentation formatting is deliberately separate from calculation.
   * This function does not change the authoritative internal value.
   */
  function formatCubicYards(value, decimalPlaces) {
    const places =
      Number.isInteger(decimalPlaces) && decimalPlaces >= 0
        ? decimalPlaces
        : 2;

    if (!Number.isFinite(value) || value < 0) {
      return '';
    }

    return value.toFixed(places);
  }

  function createInitialArea(areaId, number) {
    return {
      id: areaId,
      number,
      name: '',
      inputs: {
        measurementMethod: null,
        measurementInputsByMethod: {},
        materialId: null,
        purchaseMethod: null,
        depthInches: null,
        identicalAreaMultiplier: 1,
        relatedMaterials: {
          fabric: {
            include: null,
            allowancePercent: 15
          },
          staples: {
            include: null,
            spacingChoice: '8',
            customSpacingFeet: null
          },
          alphaEdge: {
            include: null,
            boundaryBasis: null,
            manualBoundary: {
              feet: '',
              inches: ''
            },
            additionalStraightConnectorsPerArea: null,
            ninetyDegreeConnectorsPerArea: null
          }
        }
      }
    };
  }

  /**
   * Creates one authoritative, versioned client-side input state.
   * Derived values are intentionally absent.
   */
  function createProjectState(options) {
    const settings = options || {};
    const idFactory =
      typeof settings.idFactory === 'function'
        ? settings.idFactory
        : createId;

    const projectId = idFactory('project');
    const areaId = idFactory('area');

    return {
      schemaVersion: SCHEMA_VERSION,
      configRevision: CONFIG_REVISION,
      project: {
        id: projectId,
        name:
          typeof settings.projectName === 'string'
            ? settings.projectName
            : ''
      },
      areas: [
        createInitialArea(areaId, 1)
      ],
      nextAreaNumber: 2,
      ui: {
        activeAreaId: areaId
      }
    };
  }

  function updateProjectName(state, projectName) {
    if (!state || !state.project) {
      throw new TypeError('A valid project state is required.');
    }

    return {
      ...state,
      project: {
        ...state.project,
        name: typeof projectName === 'string' ? projectName : ''
      }
    };
  }

  function updateAreaInputs(state, areaId, inputPatch) {
    if (!state || !Array.isArray(state.areas)) {
      throw new TypeError('A valid project state is required.');
    }

    const areaIndex = state.areas.findIndex(function (area) {
      return area.id === areaId;
    });

    if (areaIndex === -1) {
      throw new RangeError('Area not found.');
    }

    const currentArea = state.areas[areaIndex];
    const patch = inputPatch || {};

    const nextMeasurementInputsByMethod =
      Object.prototype.hasOwnProperty.call(
        patch,
        'measurementInputsByMethod'
      )
        ? {
            ...currentArea.inputs.measurementInputsByMethod,
            ...patch.measurementInputsByMethod
          }
        : currentArea.inputs.measurementInputsByMethod;

    const nextArea = {
      ...currentArea,
      inputs: {
        ...currentArea.inputs,
        ...patch,
        measurementInputsByMethod:
          nextMeasurementInputsByMethod
      }
    };

    const areas = state.areas.slice();
    areas[areaIndex] = nextArea;

    return {
      ...state,
      areas
    };
  }

  function updateAreaRelatedMaterials(
    state,
    areaId,
    relatedPatch
  ) {
    if (!state || !Array.isArray(state.areas)) {
      throw new TypeError('A valid project state is required.');
    }

    const areaIndex = state.areas.findIndex(function (area) {
      return area.id === areaId;
    });

    if (areaIndex === -1) {
      throw new RangeError('Area not found.');
    }

    const currentArea = state.areas[areaIndex];
    const currentRelated =
      currentArea.inputs.relatedMaterials || {};
    const patch = relatedPatch || {};

    const nextRelated = {
      ...currentRelated
    };

    ['fabric', 'staples', 'alphaEdge'].forEach(
      function (key) {
        if (
          !Object.prototype.hasOwnProperty.call(
            patch,
            key
          )
        ) {
          return;
        }

        nextRelated[key] = {
          ...(currentRelated[key] || {}),
          ...(patch[key] || {})
        };

        if (
          key === 'alphaEdge' &&
          patch.alphaEdge &&
          Object.prototype.hasOwnProperty.call(
            patch.alphaEdge,
            'manualBoundary'
          )
        ) {
          nextRelated.alphaEdge.manualBoundary = {
            ...(
              currentRelated.alphaEdge &&
              currentRelated.alphaEdge.manualBoundary
                ? currentRelated.alphaEdge.manualBoundary
                : {}
            ),
            ...(patch.alphaEdge.manualBoundary || {})
          };
        }
      }
    );

    return updateAreaInputs(
      state,
      areaId,
      {
        relatedMaterials: nextRelated
      }
    );
  }

  function normalizeIdenticalAreaMultiplier(value) {
    if (
      value === null ||
      value === undefined ||
      (typeof value === 'string' && value.trim() === '')
    ) {
      return Object.freeze({
        status: MEASUREMENT_STATUS.INCOMPLETE,
        multiplier: null
      });
    }

    const numericValue = Number(value);

    if (
      !Number.isFinite(numericValue) ||
      !Number.isInteger(numericValue) ||
      numericValue <= 0
    ) {
      return Object.freeze({
        status: MEASUREMENT_STATUS.INVALID,
        multiplier: null
      });
    }

    return Object.freeze({
      status: MEASUREMENT_STATUS.VALID,
      multiplier: numericValue
    });
  }

  function updateAreaName(state, areaId, areaName) {
    if (!state || !Array.isArray(state.areas)) {
      throw new TypeError('A valid project state is required.');
    }

    const areaIndex = state.areas.findIndex(function (area) {
      return area.id === areaId;
    });

    if (areaIndex === -1) {
      throw new RangeError('Area not found.');
    }

    const areas = state.areas.slice();

    areas[areaIndex] = {
      ...areas[areaIndex],
      name: typeof areaName === 'string' ? areaName : ''
    };

    return {
      ...state,
      areas
    };
  }

  function addArea(state, options) {
    if (!state || !Array.isArray(state.areas)) {
      throw new TypeError('A valid project state is required.');
    }

    const settings = options || {};
    const idFactory =
      typeof settings.idFactory === 'function'
        ? settings.idFactory
        : createId;

    const areaId = idFactory('area');

    if (
      state.areas.some(function (area) {
        return area.id === areaId;
      })
    ) {
      throw new Error('Area ID must be unique.');
    }

    return {
      ...state,
      areas: state.areas.concat(
        createInitialArea(areaId, state.nextAreaNumber || state.areas.length + 1)
      ),
      nextAreaNumber: (state.nextAreaNumber || state.areas.length + 1) + 1,
      ui: {
        ...state.ui,
        activeAreaId: areaId
      }
    };
  }

  function removeArea(state, areaId) {
    if (!state || !Array.isArray(state.areas)) {
      throw new TypeError('A valid project state is required.');
    }

    const areaIndex = state.areas.findIndex(function (area) {
      return area.id === areaId;
    });

    if (areaIndex === -1) {
      throw new RangeError('Area not found.');
    }

    if (state.areas.length <= 1) {
      const replacementId = createId('area');
      return {
        ...state,
        areas: [createInitialArea(replacementId, state.nextAreaNumber || 2)],
        nextAreaNumber: (state.nextAreaNumber || 2) + 1,
        ui: {...state.ui, activeAreaId: replacementId}
      };
    }

    const areas = state.areas.filter(function (area) {
      return area.id !== areaId;
    });

    const nextActiveAreaId =
      state.ui &&
      state.ui.activeAreaId !== areaId
        ? state.ui.activeAreaId
        : areas[
            Math.min(
              areaIndex,
              areas.length - 1
            )
          ].id;

    return {
      ...state,
      areas,
      ui: {
        ...state.ui,
        activeAreaId: nextActiveAreaId
      }
    };
  }

  function deriveAreaContribution(area, measurementInput) {
    const derived = deriveArea(area, measurementInput);
    const multiplier = normalizeIdenticalAreaMultiplier(
      area && area.inputs
        ? area.inputs.identicalAreaMultiplier
        : null
    );

    const materialId =
      area &&
      area.inputs &&
      typeof area.inputs.materialId === 'string' &&
      area.inputs.materialId.trim() !== ''
        ? area.inputs.materialId.trim()
        : null;

    if (
      derived.status === MEASUREMENT_STATUS.INVALID ||
      multiplier.status === MEASUREMENT_STATUS.INVALID
    ) {
      return Object.freeze({
        status: MEASUREMENT_STATUS.INVALID,
        areaId: area && area.id ? area.id : null,
        materialId,
        multiplier: null,
        cubicYards: null
      });
    }

    if (
      derived.status !== MEASUREMENT_STATUS.VALID ||
      multiplier.status !== MEASUREMENT_STATUS.VALID ||
      !materialId
    ) {
      return Object.freeze({
        status: MEASUREMENT_STATUS.INCOMPLETE,
        areaId: area && area.id ? area.id : null,
        materialId,
        multiplier:
          multiplier.status === MEASUREMENT_STATUS.VALID
            ? multiplier.multiplier
            : null,
        cubicYards: null
      });
    }

    const cubicYards =
      derived.volume.cubicYards *
      multiplier.multiplier;

    if (!Number.isFinite(cubicYards)) {
      return Object.freeze({
        status: MEASUREMENT_STATUS.INVALID,
        areaId: area.id,
        materialId,
        multiplier: multiplier.multiplier,
        cubicYards: null
      });
    }

    return Object.freeze({
      status: MEASUREMENT_STATUS.VALID,
      areaId: area.id,
      materialId,
      multiplier: multiplier.multiplier,
      cubicYards
    });
  }

  function aggregateMaterialTotals(contributions) {
    if (!Array.isArray(contributions)) {
      throw new TypeError(
        'Area contributions must be an array.'
      );
    }

    const totalsByMaterial = new Map();
    const excludedAreaIds = [];

    contributions.forEach(function (contribution) {
      if (
        !contribution ||
        contribution.status !== MEASUREMENT_STATUS.VALID ||
        typeof contribution.materialId !== 'string' ||
        contribution.materialId === '' ||
        !Number.isFinite(contribution.cubicYards)
      ) {
        if (contribution && contribution.areaId) {
          excludedAreaIds.push(contribution.areaId);
        }
        return;
      }

      const key = contribution.materialId + ':' + (contribution.purchaseMethod || 'bulk');
      const existing =
        totalsByMaterial.get(key) || {
          materialId: contribution.materialId,
          purchaseMethod: contribution.purchaseMethod || 'bulk',
          cubicYards: 0,
          areaCount: 0
        };

      existing.cubicYards += contribution.cubicYards;
      existing.areaCount += 1;

      totalsByMaterial.set(
        key,
        existing
      );
    });

    return Object.freeze({
      totals: Object.freeze(
        Array.from(totalsByMaterial.values()).map(
          function (total) {
            return Object.freeze({
              materialId: total.materialId,
              purchaseMethod: total.purchaseMethod,
              cubicYards: total.cubicYards,
              areaCount: total.areaCount
            });
          }
        )
      ),
      excludedAreaIds: Object.freeze(
        excludedAreaIds.slice()
      )
    });
  }

  function deriveArea(area, measurementInput) {
    if (!area || !area.inputs) {
      return Object.freeze({
        status: MEASUREMENT_STATUS.INVALID,
        measurement: null,
        volume: null
      });
    }

    /*
     * Phase 2 geometry is deliberately external to the core engine.
     * The geometry layer supplies measurementInput.
     *
     * The legacy area.inputs.measurement fallback exists only to preserve
     * compatibility with the accepted CP-02 calculation contract while
     * Phase 2 migrates the UI to authoritative geometry inputs.
     */
    const sourceMeasurement =
      measurementInput !== undefined
        ? measurementInput
        : area.inputs.measurement;

    const measurement = normalizeMeasurement(
      sourceMeasurement
    );

    const volume = calculateCubicYards(
      measurement,
      area.inputs.depthInches
    );

    return Object.freeze({
      status: volume.status,
      measurement,
      volume
    });
  }

  return Object.freeze({
    SCHEMA_VERSION,
    CONFIG_REVISION,
    MEASUREMENT_STATUS,
    normalizeDepthInches,
    normalizeMeasurement,
    calculateCubicYards,
    formatCubicYards,
    createProjectState,
    updateProjectName,
    updateAreaName,
    updateAreaInputs,
    updateAreaRelatedMaterials,
    addArea,
    removeArea,
    normalizeIdenticalAreaMultiplier,
    deriveArea,
    deriveAreaContribution,
    aggregateMaterialTotals
  });
});
